import React, { FC, useCallback, useMemo, MouseEvent } from 'react'
import { Map, List } from 'immutable'

import {
  getSelectedBlocksList,
} from 'draftjs-utils'

import {
  EditorState,
  ContentBlock,
  Modifier,
} from 'draft-js'

import {
  ITextEditor_EditorUpdate,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  IconComponentType,
} from '../../types/app'

import ToolbarButton from './ToolbarButton'

interface ToolbarButtonBlockDataProps {
  name: string,
  value: string,
  defaultValue?: string,
  title: string,
  className?: string,
  disabledBlocks?: string[],
  icon: IconComponentType,
  editorState: EditorState,
  setEditorState: ITextEditor_EditorUpdate,
}

const ToolbarButtonBlockData: FC<React.PropsWithChildren<ToolbarButtonBlockDataProps>> = ({
  name,
  value,
  defaultValue,
  title,
  className,
  disabledBlocks = [],
  icon,
  editorState,
  setEditorState,
}) => {

  const onClick = useCallback(() => {
    const newContentState = Modifier.mergeBlockData(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      Map().set(name, value),
    )
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'change-block-data',
    )
    setEditorState(newEditorState)
  }, [
    editorState,
  ])

  const active = useMemo<boolean>(() => {
    if(!editorState || !name || !value) return false
    const blocks = getSelectedBlocksList(editorState) as ContentBlock[]
    const allValues = blocks.reduce<Record<string, boolean>>((all, block) => {
      const data = block.getData()
      const value = data.get(name)
      if(value) {
        all[value] = true
      }
      return all
    }, {})
    if(Object.keys(allValues).length > 1) return false
    if((blocks as unknown as List<ContentBlock>).size <= 1 && Object.keys(allValues).length <= 0 && defaultValue) {
      return value == defaultValue
    }
    return allValues[value] ? true : false
  }, [
    editorState,
    name,
    value,
    defaultValue,
  ])

  const disabled = useMemo<boolean>(() => {
    if(!editorState || disabledBlocks.length <= 0) return false
    const blocks = getSelectedBlocksList(editorState) as ContentBlock[]
    const allTypes = blocks.reduce<Record<string, boolean>>((all, block) => {
      all[block.getType()] = true
      return all
    }, {})
    const disabledType = Object
      .keys(allTypes)
      .find(type => disabledBlocks.indexOf(type) >= 0)
    return disabledType ? true : false
  }, [
    editorState,
    disabledBlocks,
  ])

  return (
    <ToolbarButton
      title={ title }
      className={ className }
      disabled={ disabled }
      icon={ icon }
      active={ disabled ? false : active }
      onClick={ onClick }
    />
  )
}

export default ToolbarButtonBlockData